import React, { useEffect, useRef } from 'react';
import '../common/MicroSurvey.css';
import './Study.css';

/**
 * Research instrument overlay in the MicroSurvey chrome (advisor §3.3). Cancel is not
 * allowed: no close on overlay click, no Cancel button. The message stays visible,
 * dimmed, behind the overlay.
 */
export interface StudyModalProps {
  title: string;
  step?: string;
  width: 600 | 1000;
  stage: string;
  formTestId: string;
  submitTestId: string;
  primaryLabel: string;
  canSubmit: boolean;
  hint?: string;
  onSubmit: () => void;
  children: React.ReactNode;
}

const StudyModal: React.FC<StudyModalProps> = ({ title, step, width, stage, formTestId, submitTestId, primaryLabel, canSubmit, hint, onSubmit, children }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const first = dialogRef.current?.querySelector<HTMLElement>('input, button, [tabindex]');
    (first || dialogRef.current)?.focus({ preventScroll: true });
    return () => { document.dispatchEvent(new Event('study-modal-closed')); };
  }, [stage]);
  return (
    <div className="modal-overlay study-modal-overlay">
      <div
        className={`modal ${width === 1000 ? 'micro-survey-compact' : 'micro-survey-clean'} study-modal`}
        role="dialog" aria-modal="true" aria-labelledby={`study-modal-title-${stage}`}
        data-testid="study-modal" data-stage={stage} ref={dialogRef} tabIndex={-1}
      >
        <div className="study-modal-head">
          <h3 className="modal-title" id={`study-modal-title-${stage}`}>{title}</h3>
          {step && <span className="cal-drawer-step study-step">{step}</span>}
        </div>
        <form className="survey-form" data-testid={formTestId} onSubmit={e => { e.preventDefault(); if (canSubmit) onSubmit(); }}>
          {children}
          <div className="modal-actions study-modal-actions">
            {!canSubmit && hint && <span className="study-hint">{hint}</span>}
            <button type="submit" className="btn btn-primary clean" data-testid={submitTestId} disabled={!canSubmit}>{primaryLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudyModal;

/** 0–100 slider in the MicroSurvey confidence chrome; reports whether it was touched (advisor M7). */
export const ConfidenceSlider: React.FC<{
  question?: string; value: number; touched: boolean; onChange: (v: number) => void;
  minLabel?: string; maxLabel?: string; name?: string;
}> = ({ question, value, touched, onChange, minLabel = 'Not at all sure', maxLabel = 'Completely sure', name = 'confidence' }) => (
  <div className="confidence-section study-question" data-testid="confidence-section">
    {question && <h3 className="study-q">{question}</h3>}
    <div className="confidence-slider-container">
      <div className="slider-labels"><span>{minLabel}</span><span>{maxLabel}</span></div>
      <input type="range" name={name} min={0} max={100} value={value} className="confidence-slider clean" aria-label={question || 'How sure are you'}
        onChange={e => onChange(Number(e.target.value))} onInput={e => onChange(Number((e.target as HTMLInputElement).value))}
        onMouseDown={() => onChange(value)} onTouchStart={() => onChange(value)} onKeyDown={() => onChange(value)} />
      <div className="confidence-value">{touched ? `${value}` : <span className="study-untouched">{value} — move the slider to set your answer</span>}</div>
    </div>
  </div>
);
